// frontend_react/src/components/AdminPanel/AccountingPanel.jsx

import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { AdminButton } from "../../pages/admin/action_engine";
import {
  fetchAccountingEntries,
  downloadAccountingEntriesCsv,
} from "../../services/accountingApi";
import logger from "../../utils/logger";
import { useAdminScope } from "../../context/AdminScopeContext";
import {
  AdminEmptyState,
  AdminErrorState,
  AdminLoadingState,
} from "../../pages/admin/ui_engine";
import { usePanelActions } from "../../pages/admin/panel_engine/consumer";
import AccountingSourceCard from "./accounting/AccountingSourceCard";
import {
  buildAccountingPresentation,
  buildAccountingSourceFilterOptions,
  matchesAccountingSourceFilter,
} from "./accounting/accountingEntryPresentation";

export default function AccountingPanel() {
  const [entries, setEntries] = useState([]);
  const [sourceFilter, setSourceFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const { selectedCompanyUuid } = useAdminScope();

  const loadEntries = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const { data } = await fetchAccountingEntries({
        companyUuid: selectedCompanyUuid,
      });

      setEntries(Array.isArray(data) ? data : []);
    } catch (err) {
      logger.error(err);
      setError(
        err?.response?.data?.error ||
          err?.response?.data?.msg ||
          err?.response?.data?.message ||
          JSON.stringify(err?.response?.data) ||
          err?.message ||
          "Erreur inconnue"
      );
    } finally {
      setLoading(false);
    }
  }, [selectedCompanyUuid]);

  const sourceFilterOptions = useMemo(
    () => buildAccountingSourceFilterOptions(entries),
    [entries]
  );

  const filteredEntries = useMemo(
    () =>
      entries.filter((entry) =>
        matchesAccountingSourceFilter(entry, sourceFilter)
      ),
    [entries, sourceFilter]
  );

  const presentationItems = useMemo(
    () => buildAccountingPresentation(filteredEntries),
    [filteredEntries]
  );

  const zEntriesCount = entries.filter(
    (entry) => entry.source_type === "cash_session_z"
  ).length;

  const downloadBlob = async (request, filename) => {
    try {
      const response = await request();
      const blob = new Blob([response.data], {
        type: response.headers?.["content-type"] || "text/csv;charset=utf-8",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();

      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      logger.error(err);
      setError("Impossible de télécharger l’export demandé.");
    }
  };

  usePanelActions([
    {
      key: "refresh",
      label: loading ? "Actualisation en cours" : "Actualiser les écritures",
      icon: "↻",
      onClick: loadEntries,
      disabled: loading,
    },
  ]);

  useEffect(() => {
    loadEntries();
  }, [loadEntries]);

  if (loading) {
    return (
      <AdminLoadingState message="Chargement des écritures comptables..." />
    );
  }

  return (
    <AccountingWrapper>
      {error && (
        <AdminErrorState
          message={error}
          actionLabel="Réessayer"
          onAction={loadEntries}
          compact
        />
      )}

      <Toolbar>
        <div>
          <strong>Écritures comptables</strong>
          <small>
            {entries.length} écriture(s), dont {zEntriesCount} liée(s) aux clôtures Z.
          </small>
        </div>

        <select
          value={sourceFilter}
          onChange={(event) => setSourceFilter(event.target.value)}
        >
          <option value="all">Toutes les sources</option>
          {sourceFilterOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </Toolbar>

      {presentationItems.length === 0 ? (
        <AdminEmptyState message="Aucune écriture comptable trouvée pour ce filtre." />
      ) : (
        <EntryCards>
          {presentationItems.map((item) => (
            <AccountingSourceCard
              key={item.key}
              item={item}
              formatAmount={formatEUR}
            />
          ))}
        </EntryCards>
      )}

      <CsvActions>
        <AdminButton variant="ghost" fullWidth
          type="button"
          onClick={() =>
            downloadBlob(
              () => downloadAccountingEntriesCsv({ companyUuid: selectedCompanyUuid }),
              "ecritures_comptables.csv"
            )
          }
        >
          Télécharger CSV comptable
        </AdminButton>
      </CsvActions>
    </AccountingWrapper>
  );
}

function formatEUR(value) {
  const amount = Number(value || 0);
  return `${amount.toFixed(2)} €`;
}

const AccountingWrapper = styled.div`
  color: #f7f7f7;
`;

const Toolbar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
  padding: 1rem;
  border-radius: 16px;
  background: #202a43;
  border: 1px solid rgba(255, 255, 255, 0.08);

  strong,
  small {
    display: block;
  }

  small {
    margin-top: 0.25rem;
    opacity: 0.7;
  }

  select {
    min-width: 210px;
    border: none;
    border-radius: 10px;
    padding: 0.7rem;
    background: #fff9ea;
    color: #0b1328;
    font-weight: 850;
  }
`;

const EntryCards = styled.div`
  display: grid;
  gap: 0.9rem;
`;

const CsvActions = styled.div`
  position: sticky;
  bottom: 0;
  display: grid;
  gap: 0.6rem;
  margin-top: 1rem;
  padding-top: 1rem;
  background: #1d263d;
`;
